import { supabase } from './supabase';

export type PriorityLevel = 'hot' | 'warm' | 'cold' | 'inactive';

export interface OpportunityScore {
  priority_score: number;
  priority_level: PriorityLevel;
  engagement_score: number;
  recency_score: number;
  value_score: number;
  days_in_stage: number;
  last_activity_date: string | null;
  activity_count: number;
}

export const scoringService = {
  /**
   * Recalculate priority score for a specific opportunity
   */
  async recalculateScore(opportunityId: string): Promise<void> {
    const { error } = await supabase.rpc('calculate_opportunity_priority_score', {
      p_opportunity_id: opportunityId,
    });

    if (error) throw error;
  },

  /**
   * Recalculate scores for multiple opportunities
   */
  async recalculateScores(opportunityIds: string[]): Promise<void> {
    await Promise.all(
      opportunityIds.map(id => this.recalculateScore(id))
    );
  },

  /**
   * Recalculate scores for all opportunities in a brand
   */
  async recalculateBrandScores(brandId: string): Promise<void> {
    const { data: opportunities, error } = await supabase
      .from('opportunities')
      .select('id')
      .eq('brand_id', brandId);

    if (error) throw error;
    if (!opportunities) return;

    await this.recalculateScores(opportunities.map(o => o.id));
  },

  /**
   * Get priority level configuration
   */
  getPriorityConfig(level: PriorityLevel): {
    label: string;
    color: string;
    bgColor: string;
    borderColor: string;
    icon: string;
  } {
    const configs = {
      hot: {
        label: 'Hot',
        color: 'text-red-700',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        icon: '🔥',
      },
      warm: {
        label: 'Warm',
        color: 'text-orange-700',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        icon: '🌤️',
      },
      cold: {
        label: 'Cold',
        color: 'text-blue-700',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        icon: '❄️',
      },
      inactive: {
        label: 'Inactive',
        color: 'text-gray-700',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-200',
        icon: '💤',
      },
    };

    return configs[level];
  },

  /**
   * Get score breakdown explanation
   */
  getScoreBreakdown(score: OpportunityScore): {
    component: string;
    value: number;
    weight: string;
    description: string;
  }[] {
    return [
      {
        component: 'Engagement',
        value: score.engagement_score,
        weight: '40%',
        description: `${score.activity_count} activities recorded`,
      },
      {
        component: 'Recency',
        value: score.recency_score,
        weight: '30%',
        description: score.last_activity_date
          ? `Last contact: ${this.formatDaysAgo(score.last_activity_date)}`
          : 'No recent contact',
      },
      {
        component: 'Deal Value',
        value: score.value_score,
        weight: '20%',
        description: 'Based on opportunity value',
      },
      {
        component: 'Probability',
        value: Math.round(score.priority_score * 0.1),
        weight: '10%',
        description: 'Win probability estimate',
      },
    ];
  },

  /**
   * Format days ago into human-readable string
   */
  formatDaysAgo(date: string): string {
    const now = new Date();
    const past = new Date(date);
    const diffTime = Math.abs(now.getTime() - past.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  },

  /**
   * Get recommended actions based on priority level and score
   */
  getRecommendedActions(
    priorityLevel: PriorityLevel,
    score: OpportunityScore
  ): string[] {
    const recommendations: string[] = [];

    // Recency-based recommendations
    const daysSinceActivity = score.last_activity_date
      ? Math.floor(
          (Date.now() - new Date(score.last_activity_date).getTime()) /
            (1000 * 60 * 60 * 24)
        )
      : 999;

    if (daysSinceActivity > 7) {
      recommendations.push('Schedule a follow-up call');
    }
    if (daysSinceActivity > 14) {
      recommendations.push('Send re-engagement email');
    }

    // Stage duration recommendations
    if (score.days_in_stage > 14) {
      recommendations.push('Move to next stage or update status');
    }

    // Activity-based recommendations
    if (score.activity_count < 3) {
      recommendations.push('Log more interactions to track engagement');
    }

    // Priority-specific recommendations
    if (priorityLevel === 'hot') {
      recommendations.push('Close this week - high priority!');
    } else if (priorityLevel === 'warm') {
      recommendations.push('Schedule demo or proposal presentation');
    } else if (priorityLevel === 'cold') {
      recommendations.push('Re-qualify or nurture with content');
    } else if (priorityLevel === 'inactive') {
      recommendations.push('Consider archiving or mark as lost');
    }

    return recommendations.slice(0, 3); // Return top 3
  },
};
